import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/ui/Button";
import { Field, Input } from "@/ui/Field";
import { AuthCard, FormError, FormNotice } from "@/features/auth/AuthCard";

export const Route = createFileRoute("/_public/reset-password")({
  component: ResetPasswordScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

const MIN_PASSWORD_LENGTH = 12;

function ResetPasswordScreen() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const reset = useMutation({
    mutationFn: () => api.post<{ message: string }>("/auth/password-reset/confirm", { token, password }),
  });

  const fieldErrors = reset.error instanceof ApiError ? reset.error.fieldErrors() : {};
  const mismatch = confirmation.length > 0 && confirmation !== password;

  if (!token) {
    return (
      <AuthCard title="This link is incomplete">
        <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
          The reset link is missing its token. Open the link from your email again, or{" "}
          <Link to="/forgot-password" className="text-[var(--primary)] underline">
            request a new one
          </Link>
          .
        </p>
      </AuthCard>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthCard title="Password changed">
        <div className="flex flex-col gap-4">
          <FormNotice title="Your password has been changed.">
            For safety, you have been signed out on every device.
          </FormNotice>
          <Button tone="primary" wide onClick={() => navigate({ to: "/sign-in" })}>
            Sign in
          </Button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a new password" subtitle="This will sign you out everywhere.">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          reset.mutate();
        }}
      >
        <Field
          label="New password"
          required
          error={fieldErrors.password}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              autoFocus
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby={describedBy}
              invalid={invalid}
            />
          )}
        </Field>

        <Field label="Confirm new password" required error={mismatch ? "The two passwords do not match." : undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              aria-describedby={describedBy}
              invalid={invalid}
            />
          )}
        </Field>

        {reset.error && !Object.keys(fieldErrors).length && <FormError error={reset.error} />}

        <Button
          type="submit"
          tone="primary"
          wide
          busy={reset.isPending}
          disabled={mismatch || password.length < MIN_PASSWORD_LENGTH}
        >
          Change password
        </Button>
      </form>
    </AuthCard>
  );
}
